import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import configVars from "../config";
import SingleNote from "./SingleNote";
import { Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import { useSelector, useDispatch } from "react-redux";
import { ADD_BOARD_NOTE, UPDATE_BOARD_NOTE } from "../redux/types";
import { getNotesFromApi } from "../redux/actions/board-notes";
import { getExpiry } from "../common";
import { useCookies } from "react-cookie";
import jwt_decode from "jwt-decode";
import useExitPrompt from "../utils/useExitPrompt";
import { Prompt } from "react-router-dom";
import PromptBox from "./PromptBox";

const BoardNotes = ({ userid, token, boardid, board }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [translation] = useTranslation("translations");
  const [boardNoteTitle, setboardNoteTitle] = useState({
    value: "",
    error: false,
  });
  const [noteContent, setNoteContent] = useState("");
  const [noteContentState, setNoteContentState] = useState("");
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const allNotesData = useSelector((state) => state.boardNotes.allBoardNotes);
  const [editMode, setEditMode] = useState(false);
  const [displayPrompt, setDisplayPrompt] = useState(false);

  const dispatch = useDispatch();
  const [cookies] = useCookies(["sustainyou_user_jwt"]);
  const [editNoteIndex, setEditNoteIndex] = useState(-1);
  const [isBlocking, setIsBlocking] = useState(false);
  const [showExitPrompt, setShowExitPrompt] = useExitPrompt(false);

  useEffect(() => {
    async function getNotes() {
      let expired = getExpiry(
        `sustainchange_single_board_note_expiry_${boardid}`
      );

      if (cookies.sustainyou_user_jwt) {
        const jwtDecoded = jwt_decode(cookies.sustainyou_user_jwt);
        if (jwtDecoded && expired) {
          setIsLoadingNotes(true);
          await dispatch(getNotesFromApi(boardid));
          setIsLoadingNotes(false);
        } else {
          await dispatch(getNotesFromApi(boardid));
        }
      }
    }
    getNotes();
  }, [boardid, cookies, dispatch]);

  const handleChange = (value, field) => {
    setShowCloseConfirmation(true);
    if (field === "title") {
      setboardNoteTitle({ value: value, error: false });
    } else if (field === "noteContent") {
      setNoteContent(value);
    }
  };
  const handleClose = () => {
    setDisplayPrompt(false);
    resetState();
  };
  const setShowCloseConfirmation = (val) => {
    setIsBlocking(val);
    setShowExitPrompt(val);
  };
  const resetState = () => {
    setShowCloseConfirmation(false);
    setShowExitPrompt(false);
    setboardNoteTitle({ value: "", error: false });
    setNoteContentState("");
    setNoteContent("");
  };
  const handleClosePrompt = () => {
    console.log("handle close prompt");
    setDisplayPrompt(false);
    setIsBlocking(false);
    setShowExitPrompt(false);
    setEditMode(false);
    resetState();
  };

  const _handleAddUpdateBoardNotes = async ({
    note_id = 0,
    title,
    description,
    loadingCallback,
  }) => {
    if (title?.length === 0) {
      setIsError(true);
      setErrorMsg(translation("title_error"));
      return;
    }
    if (loadingCallback) {
      loadingCallback(true);
    } else {
      setIsLoading(true);
    }
    try {
      setErrorMsg("");
      const response = await fetch(
        `${configVars.apiUrl}sustainchange/v1/create-board-notes`,
        {
          method: "POST",
          headers: {
            Authorization: `Basic ${configVars.auth}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: token,
            id: note_id,
            board_id: boardid,
            title: title,
            content: draftToHtml(description),
          }),
        }
      );

      const data = await response.json();

      if (data.status_code === 200) {
        resetState();
        if (note_id === 0) {
          dispatch({
            type: ADD_BOARD_NOTE,
            payload: { notes: data?.data, boardId: boardid },
          });
        } else {
          dispatch({
            type: UPDATE_BOARD_NOTE,
            payload: { notes: data?.data, boardId: boardid, noteId: note_id },
          });
          setEditNoteIndex(-1);
        }
      } else {
        setIsError(true);
        setErrorMsg(translation("purpose_update_error_message"));
        setTimeout(() => {
          setIsError(false);
          setErrorMsg("");
        }, 3000);
      }
    } catch (error) {
      console.log({ error });
    }
    resetState();
    setDisplayPrompt(false);
    setEditNoteIndex(-1);
    setIsLoading(false);
    setEditMode(false);
    if (loadingCallback) {
      loadingCallback(false);
    }
  };

  return (
    <div>
      <Prompt
        when={showExitPrompt}
        message={translation("form_close_confirmation")}
      />
      <div className="col-12 align-items-center justify-content-between mb-2 board-notes">
        <div className="d-flex justify-content-between">
          <h2>{translation("board_notes_heading")} </h2>
          <Button
            onClick={() => {
              if (!editMode) {
                setEditMode(true);
                return;
              }
              if (isBlocking) {
                setDisplayPrompt(true);
              }
              setIsError(false);
              setErrorMsg("");
              setEditMode(false);
            }}
            disabled={isLoading}
            className="text-white rounded btn btn-sm pull-right btn-halite-blue-light"
          >
            {editMode ? translation("cancel") : translation("+ Add Note")}
          </Button>
        </div>
        {editMode && (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                <b className="text-capitalize">{translation("title")}</b>
              </Form.Label>
              <Form.Control
                type="text"
                value={boardNoteTitle?.value?.replaceAll("&amp;", "&")}
                onChange={(e) => handleChange(e.target.value, "title")}
                style={{ fontSize: "14px", padding: "0.5rem 0.5rem" }}
              />
              {boardNoteTitle?.error && (
                <Form.Control.Feedback type="invalid">
                  {translation("task_forms.action_plan.field1.error")}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group>
              <Editor
                toolbarOnFocus
                toolbar={configVars?.wysiwygToolbarOptions}
                editorState={noteContent}
                toolbarClassName="toolbarClassName"
                wrapperClassName="wrapperClassName"
                editorClassName="editor-wrapper"
                onEditorStateChange={(e) => handleChange(e, "noteContent")}
                onChange={(e) => {
                  setNoteContentState(e);
                }}
              />
            </Form.Group>
            <div className="row mt-2">
              <div className="col-6">
                {isError && <span className="text-danger">{errorMsg}</span>}
              </div>
              <div className="col-6">
                <Button
                  onClick={() => {
                    _handleAddUpdateBoardNotes({
                      note_id: 0,
                      title: boardNoteTitle?.value,
                      description: noteContentState,
                    });
                  }}
                  disabled={isLoading}
                  className="text-white rounded btn btn-halite-blue-light btn-sm pull-right"
                >
                  {isLoading ? (
                    <Spinner
                      animation="border"
                      size="sm"
                      style={{ color: "#fff" }}
                    />
                  ) : (
                    <>{translation("buttons.save")}</>
                  )}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </div>

      <div className="mt-2 pt-2">
        {isLoadingNotes ? (
          <div className="d-flex justify-content-center mt-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          allNotesData &&
          allNotesData[boardid] &&
          allNotesData[boardid].map((note, index) => {
            return (
              <SingleNote
                userid={userid}
                key={note.note_id}
                note={note}
                index={index}
                editNoteIndex={editNoteIndex}
                setEditNoteIndex={setEditNoteIndex}
                _handleAddUpdateBoardNotes={_handleAddUpdateBoardNotes}
                setboardNoteTitle={setboardNoteTitle}
                setNoteContent={setNoteContent}
                setNoteContentState={setNoteContentState}
                isLoading={isLoading}
                token={token}
                boardId={boardid}
              />
            );
          })
        )}
      </div>
      <PromptBox
        show={displayPrompt}
        handleClosePrompt={handleClosePrompt}
        title={translation("changes_not_saved")}
        message={translation("select_changes")}
        handleClose={handleClose}
        handleSave={() => {
          _handleAddUpdateBoardNotes({
            note_id: 0,
            title: boardNoteTitle?.value,
            description: noteContentState,
          });
        }}
        isLoading={isLoading}
      />
    </div>
  );
};
export default BoardNotes;
